import { STAGE_COLORS, STAGE_FALLBACK_COLOR } from '../board/stage-colors';

/**
 * One colour per ROLL-OFF stage, shared by every roll-off surface: the board
 * headers, the card badges, and the detail page's stage history.
 *
 * ── WHY A SECOND MAP AND NOT SIX MORE KEYS IN THE FIRST ───────────────────
 *
 * Because a stage code is not unique across the product. `lead_stage` is
 * unique per (company, VOCABULARY, code), and roll-off deliberately uses three
 * codes the lead vocabulary already had — Negotiation, Contract and Lost — for
 * stages that are not the lead stages of the same name. One flat map keyed by
 * code alone cannot hold both vocabularies without asserting they mean the
 * same thing, and the day someone recolours a lead stage the roll-off board
 * would follow it for no reason anybody could find.
 *
 * There is a second, duller reason: adding Quote / Prepayment / Fulfillment to
 * the Leads palette would force roll-off codes into a vocabulary that only
 * describes Leads.
 *
 * ── WHY THE HUES ARE STILL SHARED WHERE THE CONCEPT IS ────────────────────
 *
 * Two maps, one product. Where a roll-off stage means to the eye what a lead
 * stage means — terms being agreed, paperwork out, the deal ended one way or
 * the other — it takes the SAME value, by reference rather than by a copied
 * hex string, so the two cannot drift apart. Fulfillment takes the lead Won's
 * green: it is this vocabulary's winning stage, and "this column ends in a
 * sale" is the signal, not the word.
 *
 * Quote and Prepayment are new concepts and get their own hues.
 *
 * The fallback is imported rather than restated: "we have no colour for this"
 * should look the same everywhere, and a tenant's own invented stage takes it
 * on either board.
 */
export const ROLLOFF_STAGE_COLORS: Record<string, string> = {
  Quote: '#0288d1',
  Negotiation: STAGE_COLORS['Negotiation'],
  Contract: STAGE_COLORS['Contract'],
  Prepayment: '#00897b',
  Fulfillment: STAGE_COLORS['Won'],
  Lost: STAGE_COLORS['Lost'],
};

export { STAGE_FALLBACK_COLOR };

/**
 * The one way a roll-off surface asks for a colour.
 *
 * Every roll-off screen goes through this function, so a stage cannot read
 * teal on the board and grey in the detail page. Calling the LEAD `stageColor` on a
 * roll-off code would be the way that happens: three of the six codes would
 * resolve — to the lead stage's colour — and the other three would fall
 * through to grey, which looks deliberate.
 */
export function rollOffStageColor(stage: string | null | undefined): string {
  return (stage && ROLLOFF_STAGE_COLORS[stage]) || STAGE_FALLBACK_COLOR;
}
